import { foodData, categoryColors } from './foodData.js';
import { loadProgress, saveProgress, hasProgressData } from './storage.js';
import { exportAsPNG, exportAsPDF } from './export.js';
import { loadProgressFromURL, generateShareableURL, clearURLParameter } from './urlSharing.js';

export class BabyFoodTracker {
    constructor() {
        this.storageKey = 'babyFoodTrackerProgress_png';

        // Check for shared progress from URL first
        const sharedProgress = loadProgressFromURL();
        if (sharedProgress) {
            // Check if there's existing progress data
            const existingProgress = loadProgress(this.storageKey);
            const hasExistingData = hasProgressData(existingProgress);
            
            if (hasExistingData) {
                // Prompt user for confirmation before replacing existing data
                const shouldReplace = confirm(
                    'You have existing food tracking progress. Do you want to replace it with the shared progress from this link?\n\n' +
                    'Click "OK" to replace your current progress, or "Cancel" to keep your existing progress.'
                );
                
                if (shouldReplace) {
                    this.progress = sharedProgress;
                    // Save the shared progress to localStorage
                    saveProgress(this.storageKey, this.progress);
                    // Clear the URL parameter to clean up the URL
                    clearURLParameter();
                } else {
                    // Keep existing progress and clear URL parameter
                    this.progress = existingProgress;
                    clearURLParameter();
                }
            } else {
                // No existing data, safe to use shared progress
                this.progress = sharedProgress;
                // Save the shared progress to localStorage
                saveProgress(this.storageKey, this.progress);
                // Clear the URL parameter to clean up the URL
                clearURLParameter();
            }
        } else {
            this.progress = loadProgress(this.storageKey);
        }

        this.currentFilter = 'all';
        this.currentSearch = '';
        this.milestones = [25, 50, 75, 100];
        this.init();
    }

    init() {
        this.renderCategories();
        this.updateOverallProgress();
        this.bindEvents();
        this.updateAllCategoryProgress();
    }

    // Get total food count
    getTotalFoodCount() {
        return foodData.length;
    }

    // Get tried food count
    getTriedCount() {
        return Object.values(this.progress).filter(tried => tried).length;
    }

    // Get tried count for specific category
    getCategoryTriedCount(categoryName) {
        return foodData.filter(food => food.type === categoryName)
            .filter(food => this.progress[this.getFoodKey(food)] === true).length;
    }

    // Generate unique key for food item
    getFoodKey(food) {
        return `${food.en}_${food.zh}`;
    }

    // Toggle food tried status
    toggleFood(food) {
        const key = this.getFoodKey(food);
        const wasChecked = this.progress[key] === true;
        const prevTriedCount = this.getTriedCount();

        this.progress[key] = !wasChecked;
        saveProgress(this.storageKey, this.progress);

        // Update UI
        this.updateFoodCard(food);
        this.updateOverallProgress();
        this.updateAllCategoryProgress();

        // Check for milestones
        const newTriedCount = this.getTriedCount();
        if (newTriedCount > prevTriedCount) {
            this.checkMilestone(newTriedCount);
        }
    }

    // Check and show milestone if reached
    checkMilestone(count) {
        const milestone = this.milestones.find(m => m === count);
        if (milestone) {
            this.showMilestoneModal(milestone);
        }
    }

    // Show milestone celebration modal
    showMilestoneModal(milestone) {
        const modal = document.getElementById('milestone-modal');
        const title = document.getElementById('milestone-title');
        const message = document.getElementById('milestone-message');

        title.textContent = `🎉 ${milestone} Foods Tried!`;

        let messageText = '';
        switch (milestone) {
            case 25:
                messageText = "Great start! Your baby is exploring new flavors!";
                break;
            case 50:
                messageText = "Halfway there! Amazing progress on the food journey!";
                break;
            case 75:
                messageText = "Almost there! Your baby is becoming quite the foodie!";
                break;
            case 100:
                messageText = "Congratulations! 100 foods before 1 - mission accomplished! 🎊";
                break;
        }

        message.textContent = messageText;
        modal.style.display = 'block';

        // Auto-hide after 5 seconds
        setTimeout(() => {
            modal.style.display = 'none';
        }, 5000);
    }

    // Update overall progress display
    updateOverallProgress() {
        const triedCount = this.getTriedCount();
        const totalCount = this.getTotalFoodCount();
        const percentage = totalCount > 0 ? (triedCount / totalCount) * 100 : 0;

        document.getElementById('overall-count').textContent = triedCount;
        document.getElementById('overall-total').textContent = totalCount;
        document.getElementById('overall-progress-fill').style.width = `${percentage}%`;
    }

    // Update all category progress bars
    updateAllCategoryProgress() {
        const uniqueTypes = [...new Set(foodData.map(food => food.type))];
        uniqueTypes.forEach(categoryName => {
            this.updateCategoryProgress(categoryName);
        });
    }

    // Update specific category progress
    updateCategoryProgress(categoryName) {
        const triedCount = this.getCategoryTriedCount(categoryName);
        const totalCount = foodData.filter(food => food.type === categoryName).length;
        const percentage = totalCount > 0 ? (triedCount / totalCount) * 100 : 0;

        const countElement = document.querySelector(`[data-category="${categoryName}"] .category-count`);
        const fillElement = document.querySelector(`[data-category="${categoryName}"] .category-progress-fill`);

        if (countElement) {
            countElement.textContent = `${triedCount}/${totalCount}`;
        }

        if (fillElement) {
            fillElement.style.width = `${percentage}%`;
            fillElement.style.backgroundColor = categoryColors[categoryName] || '#4ecdc4';
        }
    }

    // Update individual food card appearance
    updateFoodCard(food) {
        const key = this.getFoodKey(food);
        const card = document.querySelector(`[data-food-key="${key}"]`);

        if (card) {
            const isTried = this.progress[key] === true;
            card.classList.toggle('tried', isTried);
        }
    }

    // Render all categories
    renderCategories() {
        const container = document.getElementById('food-categories');
        container.innerHTML = '';

        // Group foods by type
        const foodsByType = {};
        foodData.forEach(food => {
            if (!foodsByType[food.type]) {
                foodsByType[food.type] = [];
            }
            foodsByType[food.type].push(food);
        });

        Object.entries(foodsByType).forEach(([categoryName, foods]) => {
            const categoryElement = this.createCategoryElement(categoryName, foods);
            container.appendChild(categoryElement);
        });
    }

    // Create category element
    createCategoryElement(categoryName, foods) {
        const categoryDiv = document.createElement('div');
        categoryDiv.className = 'category';
        categoryDiv.setAttribute('data-category', categoryName);

        categoryDiv.innerHTML = `
            <div class="category-header">
                <h2 class="category-title" style="color: ${categoryColors[categoryName] || '#4a4a4a'}">${categoryName}</h2>
                <div class="category-progress">
                    <span class="category-count">0/${foods.length}</span>
                    <div class="category-progress-bar">
                        <div class="category-progress-fill"></div>
                    </div>
                </div>
            </div>
            <div class="food-grid">
                ${foods.map(food => this.createFoodCardHTML(food)).join('')}
            </div>
        `;

        return categoryDiv;
    }

    // Create food card HTML
    createFoodCardHTML(food) {
        const key = this.getFoodKey(food);
        const isTried = this.progress[key] === true;

        return `
            <div class="food-card ${isTried ? 'tried' : ''}" data-food-key="${key}">
                <img class="food-image"
                     src="${food.image}"
                     alt="${food.en} (${food.zh})"
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                <div class="food-image error" style="display: none;">🍽️</div>
                <div class="food-names">
                    <div class="food-name-en">${food.en}</div>
                    <div class="food-name-zh">${food.zh}</div>
                </div>
            </div>
        `;
    }

    // Bind all event listeners
    bindEvents() {
        // Food card clicks
        document.addEventListener('click', (e) => {
            const foodCard = e.target.closest('.food-card');
            if (foodCard) {
                const key = foodCard.getAttribute('data-food-key');
                const food = this.findFoodByKey(key);
                if (food) {
                    this.toggleFood(food);
                }
            }

            // Handle more dropdown clicks
            const moreDropdown = document.querySelector('.more-dropdown');
            const moreMenu = document.querySelector('.more-menu');
            if (moreDropdown && !moreDropdown.contains(e.target)) {
                moreMenu.classList.remove('show');
            }
        });

        // Search input
        document.getElementById('search-input').addEventListener('input', (e) => {
            this.currentSearch = e.target.value.toLowerCase().trim();
            this.applyFilters();
        });

        // Filter select
        document.getElementById('filter-select').addEventListener('change', (e) => {
            this.currentFilter = e.target.value;
            this.applyFilters();
        });

        // Action buttons
        document.getElementById('share-btn').addEventListener('click', () => this.shareProgressURL());
        document.getElementById('more-btn').addEventListener('click', () => this.toggleMoreMenu());
        document.getElementById('import-btn').addEventListener('click', () => this.importProgress());
        document.getElementById('export-json-btn').addEventListener('click', () => this.exportProgress());
        document.getElementById('export-png-btn').addEventListener('click', () => {
            exportAsPNG(() => this.generateExportLayout(), container => this.waitForImagesToLoad(container));
        });
        document.getElementById('export-pdf-btn').addEventListener('click', () => {
            exportAsPDF(() => this.generateExportLayout(), container => this.waitForImagesToLoad(container));
        });
        document.getElementById('reset-btn').addEventListener('click', () => this.resetProgress());

        // Modal close handlers
        document.querySelector('.close').addEventListener('click', () => {
            document.getElementById('milestone-modal').style.display = 'none';
        });

        document.getElementById('share-modal-close').addEventListener('click', () => {
            document.getElementById('share-modal').style.display = 'none';
        });

        // Modal background click
        document.getElementById('milestone-modal').addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                e.target.style.display = 'none';
            }
        });

        document.getElementById('share-modal').addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                e.target.style.display = 'none';
            }
        });

        // Copy URL button
        document.getElementById('copy-url-btn').addEventListener('click', () => this.copyShareURL());

        // File input for import
        document.getElementById('file-input').addEventListener('change', (e) => {
            this.handleFileImport(e);
        });

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                document.getElementById('milestone-modal').style.display = 'none';
                document.getElementById('share-modal').style.display = 'none';
            }
        });
    }

    // Find food by key
    findFoodByKey(key) {
        return foodData.find(food => this.getFoodKey(food) === key);
    }

    // Apply search and filter
    applyFilters() {
        // Group foods by type
        const foodsByType = {};
        foodData.forEach(food => {
            if (!foodsByType[food.type]) {
                foodsByType[food.type] = [];
            }
            foodsByType[food.type].push(food);
        });

        Object.entries(foodsByType).forEach(([categoryName, foods]) => {
            const categoryElement = document.querySelector(`[data-category="${categoryName}"]`);
            let visibleCount = 0;

            foods.forEach(food => {
                const key = this.getFoodKey(food);
                const card = document.querySelector(`[data-food-key="${key}"]`);

                if (card) {
                    const matchesSearch = this.matchesSearch(food);
                    const matchesFilter = this.matchesFilter(food);
                    const shouldShow = matchesSearch && matchesFilter;

                    card.style.display = shouldShow ? 'flex' : 'none';
                    if (shouldShow) visibleCount++;
                }
            });

            // Hide category if no visible foods
            if (categoryElement) {
                categoryElement.style.display = visibleCount > 0 ? 'block' : 'none';
            }
        });
    }

    // Check if food matches search
    matchesSearch(food) {
        if (!this.currentSearch) return true;

        const searchLower = this.currentSearch.toLowerCase();
        return food.en.toLowerCase().includes(searchLower) ||
               food.zh.includes(this.currentSearch);
    }

    // Check if food matches filter
    matchesFilter(food) {
        const key = this.getFoodKey(food);
        const isTried = this.progress[key] === true;

        switch (this.currentFilter) {
            case 'tried':
                return isTried;
            case 'not-tried':
                return !isTried;
            default:
                return true;
        }
    }

    // Export progress as JSON
    exportProgress() {
        try {
            const exportData = {
                progress: this.progress,
                exportDate: new Date().toISOString(),
                totalFoods: this.getTotalFoodCount(),
                triedFoods: this.getTriedCount()
            };

            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });

            const link = document.createElement('a');
            link.href = URL.createObjectURL(dataBlob);
            link.download = `baby-food-progress-${new Date().toISOString().split('T')[0]}.json`;
            link.click();

            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export failed. Please try again.');
        }
    }

    // Toggle more dropdown menu
    toggleMoreMenu() {
        const moreMenu = document.querySelector('.more-menu');
        moreMenu.classList.toggle('show');
    }

    // Share progress via URL
    shareProgressURL() {
        try {
            const shareableURL = generateShareableURL(this.progress);
            document.getElementById('share-url-input').value = shareableURL;
            document.getElementById('share-modal').style.display = 'block';
        } catch (error) {
            console.error('Share URL generation failed:', error);
            alert('Failed to generate share URL. Please try again.');
        }
    }

    // Copy share URL to clipboard
    copyShareURL() {
        const urlInput = document.getElementById('share-url-input');
        try {
            urlInput.select();
            urlInput.setSelectionRange(0, 99999); // For mobile devices

            if (navigator.clipboard && window.isSecureContext) {
                // Use modern clipboard API if available
                navigator.clipboard.writeText(urlInput.value).then(() => {
                    this.showCopySuccess();
                }).catch(() => {
                    // Fallback to document.execCommand
                    this.fallbackCopyToClipboard(urlInput.value);
                });
            } else {
                // Fallback for older browsers
                this.fallbackCopyToClipboard(urlInput.value);
            }
        } catch (error) {
            console.error('Copy failed:', error);
            alert('Failed to copy URL. Please copy it manually.');
        }
    }

    // Fallback copy method for older browsers
    fallbackCopyToClipboard(text) {
        const urlInput = document.getElementById('share-url-input');
        try {
            document.execCommand('copy');
            this.showCopySuccess();
        } catch (err) {
            console.error('Fallback copy failed:', err);
            alert('Failed to copy URL. Please copy it manually.');
        }
    }

    // Show copy success feedback
    showCopySuccess() {
        const copyBtn = document.getElementById('copy-url-btn');
        const originalText = copyBtn.textContent;
        copyBtn.textContent = 'Copied!';
        copyBtn.style.background = '#4ecdc4';

        setTimeout(() => {
            copyBtn.textContent = originalText;
            copyBtn.style.background = '';
        }, 2000);
    }

    // Generate export layout HTML
    generateExportLayout() {
        const triedCount = this.getTriedCount();
        const totalCount = this.getTotalFoodCount();
        const exportDate = new Date().toLocaleDateString();

        let html = `
            <div class="export-layout">
                <div class="export-header">
                    <h1 class="export-title">Foods Before 1</h1>
                    <p class="export-progress">Progress: ${triedCount}/${totalCount} foods tried</p>
                    <p class="export-date">Exported on ${exportDate}</p>
                </div>
        `;

        // Group foods by type
        const foodsByType = {};
        foodData.forEach(food => {
            if (!foodsByType[food.type]) {
                foodsByType[food.type] = [];
            }
            foodsByType[food.type].push(food);
        });

        // Generate categories
        Object.entries(foodsByType).forEach(([categoryName, foods]) => {
            const categoryTriedCount = this.getCategoryTriedCount(categoryName);
            const categoryColor = categoryColors[categoryName];

            html += `
                <div class="export-category">
                    <div class="export-category-header" style="background-color: ${categoryColor}">
                        <h2 class="export-category-title">${categoryName}</h2>
                        <span class="export-category-progress">${categoryTriedCount}/${foods.length}</span>
                    </div>
                    <div class="export-food-grid">
            `;

            // Generate food cards
            foods.forEach(food => {
                const key = this.getFoodKey(food);
                const isTried = this.progress[key] === true;
                const triedClass = isTried ? 'tried' : '';

                html += `
                    <div class="export-food-card ${triedClass}">
                        <img src="${food.image}" alt="${food.en} (${food.zh})" class="export-food-image"
                             onerror="this.style.display='none'; this.nextElementSibling.style.display='flex'">
                        <div class="export-food-image error" style="display: none;">🍽️</div>
                        <div class="export-food-names">
                            <div class="export-food-name-en">${food.en}</div>
                            <div class="export-food-name-zh">${food.zh}</div>
                        </div>
                    </div>
                `;
            });

            html += `
                    </div>
                </div>
            `;
        });

        html += '</div>';
        return html;
    }

    // Helper: Wait for all images in a container to load
    async waitForImagesToLoad(container) {
        const images = Array.from(container.querySelectorAll('img'));
        await Promise.all(images.map(img => {
            if (img.complete && img.naturalWidth !== 0) return Promise.resolve();
            return new Promise(resolve => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
            });
        }));
    }

    // Import progress from file
    importProgress() {
        document.getElementById('file-input').click();
    }

    // Handle file import
    handleFileImport(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);

                if (data.progress && typeof data.progress === 'object') {
                    this.progress = data.progress;
                    saveProgress(this.storageKey, this.progress);
                    this.updateUI();
                    alert('Progress imported successfully!');
                } else {
                    throw new Error('Invalid file format');
                }
            } catch (error) {
                console.error('Import failed:', error);
                alert('Import failed. Please check the file format.');
            }
        };

        reader.readAsText(file);
        event.target.value = ''; // Reset file input
    }

    // Reset all progress
    resetProgress() {
        if (confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
            this.progress = {};
            saveProgress(this.storageKey, this.progress);
            this.updateUI();
        }
    }

    // Update entire UI
    updateUI() {
        // Re-render categories to update tried states
        this.renderCategories();
        this.updateOverallProgress();
        this.updateAllCategoryProgress();

        // Re-apply current filters
        if (this.currentSearch || this.currentFilter !== 'all') {
            this.applyFilters();
        }
    }
}
